import React from 'react';
import { X, LogOut, User as UserIcon, Palette, Shield, Lock as LockIcon, Volume2, Video, Smartphone } from 'lucide-react';
import ProfilePreview from '../../ProfilePreview';
import ProfileCustomizer from '../ProfileCustomizer';
import { generateMSNAvatar } from '../../utils/uiUtils';
import { updateProfile } from '../../utils/supabase/profile';
import { entityResolver } from '../../core/resolvers/EntityResolver';

export interface UserSettingsModalProps {
  showUserSettings: boolean;
  setShowUserSettings: (show: boolean) => void;
  activeSettingsCategory: string;
  setActiveSettingsCategory: (category: string) => void;
  draftProfile: any;
  setDraftProfile: React.Dispatch<React.SetStateAction<any>>;
  userProfile: any;
  setUserProfile: (profile: any) => void;
  currentUser: any;
  handleLogout: () => void;
  allowFriendFrom: 'everyone' | 'shared_servers' | 'mutual_friends' | 'nobody';
  setAllowFriendFrom: React.Dispatch<React.SetStateAction<'everyone' | 'shared_servers' | 'mutual_friends' | 'nobody'>>;
  blockedUsers: string[];
  setBlockedUsers: React.Dispatch<React.SetStateAction<string[]>>;
  contacts: any[];
  selectedAudioDevice: string;
  setSelectedAudioDevice: (id: string) => void;
  audioDevices: MediaDeviceInfo[];
  outputGain: number;
  setOutputGain: (gain: number) => void;
  selectedVideoDevice: string;
  setSelectedVideoDevice: (id: string) => void;
  videoDevices: MediaDeviceInfo[];
  isPushToTalkEnabled: boolean;
  setIsPushToTalkEnabled: (enabled: boolean) => void;
  pushToTalkHotkey: string;
  setPushToTalkHotkey: (key: string) => void;
  addNotification: (notification: any) => void;
}

export const UserSettingsModal: React.FC<UserSettingsModalProps> = ({
  showUserSettings,
  setShowUserSettings,
  activeSettingsCategory,
  setActiveSettingsCategory,
  draftProfile,
  setDraftProfile,
  userProfile,
  setUserProfile,
  currentUser,
  handleLogout,
  allowFriendFrom,
  setAllowFriendFrom,
  blockedUsers,
  setBlockedUsers,
  contacts,
  selectedAudioDevice,
  setSelectedAudioDevice,
  audioDevices,
  outputGain,
  setOutputGain,
  selectedVideoDevice,
  setSelectedVideoDevice,
  videoDevices,
  isPushToTalkEnabled,
  setIsPushToTalkEnabled,
  pushToTalkHotkey,
  setPushToTalkHotkey,
  addNotification,
}) => {
  if (!showUserSettings) return null;

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 md:p-8">
      <div className="bg-[#0b0e14] border border-white/10 rounded-2xl w-full max-w-[1200px] h-full max-h-[90vh] flex flex-col shadow-2xl overflow-hidden relative">
        <input 
          type="file" 
          id="user-avatar-upload-direct" 
          className="hidden" 
          accept="image/*" 
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              const reader = new FileReader();
              reader.onload = (event) => setDraftProfile((prev: any) => ({ ...prev, avatar: event.target?.result as string }));
              reader.readAsDataURL(file);
            }
          }} 
        />
        <input 
          type="file" 
          id="user-banner-upload-direct" 
          className="hidden" 
          accept="image/*" 
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              const reader = new FileReader();
              reader.onload = (event) => setDraftProfile((prev: any) => ({ ...prev, bannerImage: event.target?.result as string, bannerType: 'image' }));
              reader.readAsDataURL(file);
            }
          }} 
        />
        
        {/* Close Button */}
        <button onClick={() => setShowUserSettings(false)} className="absolute top-3 right-3 lg:top-4 lg:right-4 text-white/70 hover:text-white z-[200] bg-slate-900/90 border border-white/20 p-2 rounded-full backdrop-blur-md transition-colors hover:bg-white/20 shadow-lg">
          <X size={18} />
        </button>

        <div className="flex flex-col lg:flex-row h-full overflow-hidden w-full">
          {/* 1. Sidebar Navigation */}
          <div className="w-full lg:w-64 h-auto lg:h-full bg-black/40 border-b lg:border-b-0 lg:border-r border-white/5 flex flex-row lg:flex-col p-2 pr-14 lg:p-4 shrink-0 overflow-x-auto lg:overflow-y-auto no-scrollbar items-center lg:items-stretch gap-2 z-10 relative">
            <div className="hidden lg:block px-3 mb-4 mt-2">
              <h2 className="text-xs font-black text-white/50 uppercase tracking-[0.2em]">User Settings</h2>
            </div>
            <div className="flex flex-row lg:flex-col gap-2 lg:space-y-1 lg:flex-1">
              {[
              { id: 'Profile', label: 'Profile', icon: UserIcon },
              { id: 'Appearance', label: 'Appearance', icon: Palette },
              { id: 'Account & Security', label: 'Account & Security', icon: Shield },
              { id: 'Privacy', label: 'Privacy', icon: LockIcon },
              { id: 'Audio', label: 'Audio', icon: Volume2 },
              { id: 'Video', label: 'Video', icon: Video },
              { id: 'Devices', label: 'Devices', icon: Smartphone }
              ].map(cat => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.id}
                    onClick={(e) => { e.preventDefault(); setActiveSettingsCategory(cat.id); }}
                    className={`shrink-0 lg:w-full flex items-center gap-2 lg:gap-3 text-left text-sm font-bold p-2 lg:p-3 rounded-xl transition-all ${
                      activeSettingsCategory === cat.id 
                        ? 'bg-[var(--neon)]/10 text-[var(--neon)] border border-[var(--neon)]/20 shadow-[0_0_15px_color-mix(in_srgb,var(--neon)_15%,transparent)]' 
                        : 'text-white/60 hover:text-white hover:bg-white/5 border border-transparent'
                    }`}
                  >
                    <Icon size={16} className="shrink-0" />
                    <span className="whitespace-nowrap">{cat.label}</span>
                  </button>
                )
              })}
            </div>

            {/* Persistent Logout — anchored in bottom left corner of sidebar on desktop */}
            <div className="ml-2 lg:ml-0 lg:mt-auto lg:pt-4 lg:border-t border-white/10 shrink-0">
              <button
                onClick={(e) => { e.preventDefault(); handleLogout(); }}
                className="shrink-0 lg:w-full flex items-center justify-center gap-2 bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white border border-red-500/30 px-4 py-2 lg:py-3 rounded-xl font-black text-xs uppercase tracking-widest transition-all"
              >
                <LogOut size={16} className="shrink-0" /> <span className="whitespace-nowrap">Logout</span>
              </button>
            </div>
          </div>

          {/* 2. Persistent Profile Preview */}
          <div className="w-full md:w-[360px] bg-[#0d111a] border-r border-white/5 p-6 flex flex-col items-center overflow-y-auto shrink-0 hidden lg:flex relative no-scrollbar">
             <div className="w-full flex flex-col items-center pb-10">
               <div className="w-full flex justify-between items-center mb-6">
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white/40">Live Identity</span>
               </div>
               <ProfilePreview profile={draftProfile} onAvatarClick={() => document.getElementById('user-avatar-upload-direct')?.click()} onBannerClick={() => document.getElementById('user-banner-upload-direct')?.click()} disableInitialAnimation={true} />
             </div>
          </div>

          {/* 3. Dynamic Settings Content */}
          <div className="flex-1 flex flex-col overflow-hidden bg-[#0b0e14]">
             <div className="flex-1 overflow-y-auto p-6 md:p-10 no-scrollbar relative">
               <div className="max-w-2xl mx-auto space-y-8 pb-10">
                  <h1 className="text-3xl font-black text-white uppercase tracking-wider mb-8">{activeSettingsCategory}</h1>
                  
                  {(activeSettingsCategory === 'Profile' || activeSettingsCategory === 'Appearance') && (
                     <ProfileCustomizer 
                       draftProfile={draftProfile} 
                       setDraftProfile={setDraftProfile} 
                       category={activeSettingsCategory}
                     />
                  )}

                  {activeSettingsCategory === 'Account & Security' && (
                    <div className="space-y-8">
                      <div className="space-y-4 bg-white/5 p-6 rounded-[2rem] border border-white/5">
                        <label className="text-white/30 text-[10px] font-black uppercase tracking-[0.2em] font-space">Switch Accounts</label>
                        <div className="space-y-3">
                          <p className="text-white/40 text-sm">Quickly switch between your accounts without logging out.</p>
                          <button
                            onClick={() => addNotification({ type: 'system', title: 'Switch Accounts', message: 'Multi-account support coming soon.' })}
                            className="w-full bg-white/10 hover:bg-white/20 text-white px-4 py-4 rounded-xl font-bold text-sm transition-all border border-white/10"
                          >
                            Manage Accounts
                          </button>
                        </div>
                      </div>

                      <div className="space-y-4 bg-white/5 p-6 rounded-[2rem] border border-white/5">
                        <label className="text-white/30 text-[10px] font-black uppercase tracking-[0.2em] font-space">Credentials</label>
                        <div className="space-y-4 mt-2">
                          <div className="space-y-2">
                            <label className="text-white/50 text-[9px] font-bold uppercase tracking-widest ml-1">Email Address</label>
                            <input type="text" defaultValue={currentUser?.email || ''} readOnly className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-4 text-white/50 cursor-not-allowed text-sm" />
                          </div>
                          <div className="flex gap-4">
                            <button className="flex-1 bg-white/10 hover:bg-white/20 text-white px-4 py-3 rounded-xl font-bold text-sm transition-all border border-white/10">Change Password</button>
                            <button className="flex-1 bg-white/10 hover:bg-white/20 text-white px-4 py-3 rounded-xl font-bold text-sm transition-all border border-white/10">Enable 2FA</button>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4 bg-red-500/5 p-6 rounded-[2rem] border border-red-500/10">
                        <label className="text-red-400/50 text-[10px] font-black uppercase tracking-[0.2em] font-space">Danger Zone</label>
                        <button
                          onClick={() => addNotification({ type: 'system', title: 'Deactivate Account', message: 'Backend wire-up pending.' })}
                          className="w-full flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/20 px-4 py-4 rounded-xl font-black text-xs uppercase tracking-widest transition-all"
                        >
                          Deactivate Account
                        </button>
                        <button
                          onClick={() => addNotification({ type: 'system', title: 'Delete Account', message: 'Backend wire-up pending — this cannot be undone.' })}
                          className="w-full flex items-center justify-center gap-2 bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white border border-red-500/30 px-4 py-4 rounded-xl font-black text-xs uppercase tracking-widest transition-all"
                        >
                          Delete Account
                        </button>
                      </div>
                    </div>
                  )}

                  {activeSettingsCategory === 'Privacy' && (
                    <div className="space-y-8">
                      <div className="space-y-4 bg-white/5 p-6 rounded-[2rem] border border-white/5">
                        <label className="text-white/30 text-[10px] font-black uppercase tracking-[0.2em] font-space">Friend Requests</label>
                        <div className="space-y-2">
                          {([
                            { id: 'everyone',         label: 'Everyone',         desc: 'Anyone on the platform can send you a friend request.' },
                            { id: 'shared_servers',   label: 'Shared Servers',   desc: 'Only people in servers you share with you.' },
                            { id: 'mutual_friends',   label: 'Mutual Friends',   desc: 'Only people who share a mutual friend with you.' },
                            { id: 'nobody',           label: 'Nobody',           desc: 'Disable incoming friend requests entirely.' },
                          ] as const).map(opt => (
                            <label
                              key={opt.id}
                              className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${allowFriendFrom === opt.id ? 'bg-[var(--neon)]/10 border-[var(--neon)]/30' : 'bg-black/20 border-white/5 hover:bg-white/5'}`}
                            >
                              <input
                                type="radio"
                                name="allowFriendFrom"
                                value={opt.id}
                                checked={allowFriendFrom === opt.id}
                                onChange={() => setAllowFriendFrom(opt.id)}
                                className="mt-1 accent-[var(--neon)]"
                              />
                              <div className="flex-1">
                                <p className={`font-bold text-sm ${allowFriendFrom === opt.id ? 'text-white' : 'text-white/80'}`}>{opt.label}</p>
                                <p className="text-white/40 text-xs mt-0.5">{opt.desc}</p>
                              </div>
                            </label>
                          ))}
                        </div><p className="text-white/30 text-[10px] italic">Server-side enforcement pending. This UI is a placeholder.</p>
                      </div>

                      <div className="space-y-4 bg-white/5 p-6 rounded-[2rem] border border-white/5">
                        <label className="text-white/30 text-[10px] font-black uppercase tracking-[0.2em] font-space">Blocked Users</label>
                        {blockedUsers.length === 0 ? (
                          <div className="bg-black/20 border border-white/5 rounded-xl p-8 text-center">
                            <p className="text-white/50 text-sm">You haven't blocked anyone.</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {blockedUsers.map(userId => {
                              const user = contacts.find(c => c.id === userId) || { id: userId, name: 'Unknown User', avatar: generateMSNAvatar(userId || 'Unknown') };
                              return (
                                <div key={user.id} className="flex items-center justify-between bg-black/20 border border-white/5 rounded-xl p-4">  
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full overflow-hidden border border-white/10">
                                      <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                                    </div>
                                    <span className="text-white font-bold text-sm">{user.name}</span>
                                  </div>
                                  <button onClick={() => setBlockedUsers(prev => prev.filter(id => id !== user.id))} className="text-xs font-bold bg-white/10 hover:bg-red-500/20 hover:text-red-400 text-white px-4 py-2 rounded-lg transition-colors border border-white/10 hover:border-red-500/30">Unblock</button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {activeSettingsCategory === 'Audio' && (
                    <div className="space-y-8">
                      <div className="space-y-4 bg-white/5 p-6 rounded-[2rem] border border-white/5">
                        <label className="text-white/30 text-[10px] font-black uppercase tracking-[0.2em] font-space">Audio Peripherals</label>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <label className="text-white/50 text-[9px] font-bold uppercase tracking-widest ml-1">Microphone Input</label>
                            <select
                              value={selectedAudioDevice}
                              onChange={(e) => setSelectedAudioDevice(e.target.value)}
                              className="w-full bg-[var(--os-bg)] border border-white/10 rounded-xl px-4 py-4 text-sm text-white outline-none"
                            >
                              {audioDevices.length === 0 ? <option value="default">System Default</option> : audioDevices.map((d, i) => <option key={d.deviceId} value={d.deviceId}>{d.label || `Microphone ${i + 1}`}</option>)}
                            </select>
                          </div>
                          <div className="space-y-2">
                            <label className="text-white/50 text-[9px] font-bold uppercase tracking-widest ml-1">Output Device</label>
                            <select className="w-full bg-[var(--os-bg)] border border-white/10 rounded-xl px-4 py-4 text-sm text-white outline-none">
                              <option value="default">System Default</option>
                            </select>
                            <p className="text-white/30 text-[10px] italic">Output device selection pending browser support.</p>
                          </div>
                          <div className="space-y-2">
                            <label className="text-white/50 text-[9px] font-bold uppercase tracking-widest ml-1 flex justify-between">
                              <span>Output Volume</span>
                              <span>{Math.round(outputGain * 100)}%</span>
                            </label>
                            <input
                              type="range"
                              min="0" max="1.5" step="0.01"
                              value={outputGain}
                              onChange={(e) => setOutputGain(parseFloat(e.target.value))}
                              className="w-full accent-[var(--neon)]"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4 bg-white/5 p-6 rounded-[2rem] border border-white/5">
                        <label className="text-white/30 text-[10px] font-black uppercase tracking-[0.2em] font-space">Microphone Test</label>
                        <p className="text-white/40 text-xs">Speak into your microphone to verify it's working. A real-time level meter will appear once testing begins.</p>
                        <button
                          onClick={() => addNotification({ type: 'system', title: 'Mic Test', message: 'Microphone test is currently placeholder. Full mic test UI pending backend wire-up.' })}
                          className="w-full py-3 bg-white/10 hover:bg-white/20 text-white font-bold text-xs uppercase tracking-widest rounded-xl transition-all border border-white/10"
                        >
                          Run Microphone Test
                        </button>
                      </div>

                      <div className="space-y-4 bg-white/5 p-6 rounded-[2rem] border border-white/5">
                        <label className="text-white/30 text-[10px] font-black uppercase tracking-[0.2em] font-space">Push to Talk</label>
                        <div className="space-y-4">
                          <label className="flex items-center justify-between text-white text-sm font-bold bg-black/20 p-5 rounded-xl border border-white/5 cursor-pointer">
                            Enable Push to Talk
                            <input
                              type="checkbox"
                              className="accent-[var(--neon)] w-4 h-4 cursor-pointer"
                              checked={isPushToTalkEnabled}
                              onChange={(e) => setIsPushToTalkEnabled(e.target.checked)}
                            />
                          </label>
                          {isPushToTalkEnabled && (
                            <div className="flex items-center justify-between text-white/70 text-sm bg-black/20 p-5 rounded-xl border border-white/5">
                              <span className="font-bold">PTT Hotkey</span>
                              <button
                                className="bg-white/10 border border-white/20 rounded-lg px-4 py-2 hover:border-[var(--neon)] transition-colors min-w-[120px] text-white font-bold"
                                onKeyDown={(e) => { e.preventDefault(); setPushToTalkHotkey(e.code); (e.target as HTMLElement).blur(); }}
                              >
                                {pushToTalkHotkey ? pushToTalkHotkey.replace('Key', '') : 'Click to Set'}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {activeSettingsCategory === 'Video' && (
                    <div className="space-y-8">
                      <div className="space-y-4 bg-white/5 p-6 rounded-[2rem] border border-white/5">
                        <label className="text-white/30 text-[10px] font-black uppercase tracking-[0.2em] font-space">Camera Peripherals</label>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <label className="text-white/50 text-[9px] font-bold uppercase tracking-widest ml-1">Camera Input</label>
                            <select
                              value={selectedVideoDevice}
                              onChange={(e) => setSelectedVideoDevice(e.target.value)}
                              className="w-full bg-[var(--os-bg)] border border-white/10 rounded-xl px-4 py-4 text-sm text-white outline-none"
                            >
                              {videoDevices.length === 0 ? <option value="default">System Default</option> : videoDevices.map((d, i) => <option key={d.deviceId} value={d.deviceId}>{d.label || `Camera ${i + 1}`}</option>)}
                            </select>
                          </div>
                          <button
                            onClick={() => addNotification({ type: 'system', title: 'Webcam Test', message: 'Webcam preview is currently placeholder. Real-time preview pending wire-up.' })}
                            className="w-full py-3 bg-white/10 hover:bg-white/20 text-white font-bold text-xs uppercase tracking-widest rounded-xl transition-all border border-white/10"
                          >
                            Test Webcam
                          </button>
                        </div>
                      </div>

                      <div className="space-y-4 bg-white/5 p-6 rounded-[2rem] border border-white/5">
                        <label className="text-white/30 text-[10px] font-black uppercase tracking-[0.2em] font-space">Streaming</label>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <label className="text-white/50 text-[9px] font-bold uppercase tracking-widest ml-1">Resolution</label>
                            <select className="w-full bg-[var(--os-bg)] border border-white/10 rounded-xl px-4 py-4 text-sm text-white outline-none">
                              <option value="720p">720p (HD)</option>
                              <option value="1080p">1080p (Full HD)</option>
                              <option value="1440p">1440p (QHD)</option>
                              <option value="4k">4K (2160p)</option>
                            </select>
                          </div>
                          <div className="space-y-2">
                            <label className="text-white/50 text-[9px] font-bold uppercase tracking-widest ml-1">Framerate</label>
                            <select className="w-full bg-[var(--os-bg)] border border-white/10 rounded-xl px-4 py-4 text-sm text-white outline-none">
                              <option value="30">30 FPS</option>
                              <option value="60">60 FPS</option>
                            </select>
                          </div>
                          <p className="text-white/30 text-[10px] italic">Streaming settings pending backend wire-up.</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeSettingsCategory === 'Devices' && (
                    <div className="space-y-8">
                      <div className="space-y-4 bg-white/5 p-6 rounded-[2rem] border border-white/5">
                        <label className="text-white/30 text-[10px] font-black uppercase tracking-[0.2em] font-space">Active Sessions</label>
                        <div className="space-y-2">
                          {[
                            { id: 'current', name: 'Current Session', detail: 'Windows 11 - Chrome 120.0' },
                            
                          ].map((session) => (
                            <div key={session.id} className="flex items-center justify-between bg-black/20 border border-white/5 rounded-xl p-4">
                              <div>
                                <p className="text-white font-bold text-sm">{session.name}</p>
                                <p className="text-white/50 text-xs mt-1">{session.detail}</p>
                              </div>
                              <button className="text-red-500 text-xs font-bold px-4 py-2 bg-red-500/10 rounded-lg hover:bg-red-500/20 transition-colors border border-red-500/20">
                                {session.id === 'current' ? 'Current' : 'Revoke'}
                              </button>
                            </div>
                          ))}
                        </div>
                        <p className="text-white/30 text-[10px] italic">Session data is hard-coded for layout demonstration. Backend wire-up pending.</p>
                      </div>
                    </div>
                  )}
               </div>
             </div>

             {/* Draft Actions - Persistent bottom bar ONLY if there are unsaved changes */}
             {JSON.stringify(draftProfile) !== JSON.stringify(userProfile) && (
               <div className="bg-[#121722] border-t border-[var(--neon)]/30 p-4 md:p-6 flex items-center justify-between shadow-[0_-10px_30px_rgba(0,0,0,0.5)] shrink-0 animate-in slide-in-from-bottom-10 fade-in duration-300 relative z-50">
                 <div className="flex items-center gap-3">
                   <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                   <span className="text-xs font-bold text-amber-500 uppercase tracking-widest hidden sm:block">Unsaved Changes (Draft Mode)</span>
                   <span className="text-xs font-bold text-amber-500 uppercase tracking-widest sm:hidden">Unsaved</span>
                 </div>
                 <div className="flex gap-3">
                   <button onClick={() => setDraftProfile(userProfile)} className="px-4 py-3 md:px-6 rounded-xl text-xs font-bold text-white/60 hover:text-white hover:bg-white/10 transition-colors">Reset</button>
                   <button onClick={async () => {
                      if (currentUser) {
                        try {
                          await updateProfile(currentUser.id, {
                            display_name: draftProfile.name,
                            photo_url: draftProfile.avatar,
                            status: draftProfile.status,
                            bio: draftProfile.bio,
                            customization: {
                              ...draftProfile.customization,
                              bannerType: draftProfile.bannerType,
                              bannerValue: draftProfile.bannerValue,
                              gradientHue1: draftProfile.gradientHue1,
                              gradientSat1: draftProfile.gradientSat1,
                              gradientHue2: draftProfile.gradientHue2,
                              gradientSat2: draftProfile.gradientSat2,
                              bannerImage: draftProfile.bannerImage,
                              profileBackgroundType: draftProfile.profileBackgroundType,
                              profileBackgroundValue: draftProfile.profileBackgroundValue,
                              profileBackgroundGradient: draftProfile.profileBackgroundGradient,
                              profileBackgroundImage: draftProfile.profileBackgroundImage,
                              profileTextStyle: draftProfile.profileTextStyle,
                              profileBorderStyle: draftProfile.profileBorderStyle,
                              auraMode: draftProfile.customization?.auraMode,
                              auraColor1: draftProfile.customization?.auraColor1,
                              auraColor2: draftProfile.customization?.auraColor2,
                              statusMessage: draftProfile.customization?.statusMessage,
                              // Section 5 - Modulators & LFOs
                              texture: draftProfile.customization?.texture,
                              textureIntensity: draftProfile.customization?.textureIntensity,
                              textureOpacity: draftProfile.customization?.textureOpacity,
                              lfoWaveform: draftProfile.customization?.lfoWaveform,
                              lfoRate: draftProfile.customization?.lfoRate,
                              lfoFrequency: draftProfile.customization?.lfoFrequency,
                              colorFilter: draftProfile.customization?.colorFilter,
                              audioGain: draftProfile.customization?.audioGain,
                              rgbCycleSpeed: draftProfile.customization?.rgbCycleSpeed,
                              chaoticSpeed: draftProfile.customization?.chaoticSpeed,
                            },
                          });
                          
                          setUserProfile(draftProfile);
                          entityResolver.updateUser(currentUser.id, {
                            id: currentUser.id,
                            name: draftProfile.name,
                            avatar: draftProfile.avatar,
                            customization: draftProfile.customization
                          });
                          addNotification({
                            type: 'system',
                            title: 'Profile Updated',
                            message: 'Your changes have been saved to the net.',
                          });
                        } catch (error) {
                          console.error('Profile update failed:', error);
                        }
                      }
                   }} className="px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest bg-gradient-to-r from-[var(--color-green)] to-[var(--color-cyan)] text-[#05070a] hover:scale-105 active:scale-95 transition-all glow-active">
                     Save Changes
                   </button>
                 </div>
               </div>
             )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserSettingsModal;
